import { Stack, styled } from "@mui/material";
import { Autoplay, EffectFade, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { CONTENT_GUTTER, PAGE_PADDING, gutterFor } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

/**
 * The hero slider — `nf` (`MainSlider`) and `ny` (`ModernVibrantMainSlider`).
 *
 *   isModernVibrantDesign ? <ny images/>
 *                         : <nf images isModernDesign isLargerThanMobile/>
 *
 * and when `web_slider_images` is empty the original renders a bare
 * `Stack spacing={1.5}` in its place — an empty spacer, NOT a placeholder
 * image and NOT a hidden node. Reproduced, because that spacer is what keeps
 * the tab strip off the store-status band on a tenant with no slider.
 *
 * Aspect ratio (`nm`): modern desktop **29 / 9**, modern mobile **22 / 9**,
 * everything else **16 / 9**. MODERN goes full-bleed (padding 0, radius 0);
 * the classic family is padded and rounded 20 → 24 → 28 → 32 as it widens.
 *
 * Both variants autoplay at 5000ms with `disableOnInteraction: false`, loop,
 * one slide per view, and Swiper navigation arrows themed white through the
 * four `--swiper-*` custom properties on the frame. The Modern-Vibrant one
 * adds the fade effect and its own radius ladder.
 */

/** `nv` — the outer frame, carrying the gutters and the Swiper theme vars. */
const Frame = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    zIndex: Z_INDEX.NONE,
    padding: isModernDesign ? 0 : PAGE_PADDING,
    [theme.breakpoints.up("xxs")]: {
      padding: isModernDesign ? 0 : theme.spacing(2),
    },
    [theme.breakpoints.up("sm")]: {
      padding: isModernDesign ? 0 : theme.spacing(3),
    },
    [theme.breakpoints.up("lg")]: {
      padding: isModernDesign ? 0 : `${theme.spacing(3)} ${gutter.lg}`,
    },
    [theme.breakpoints.up("xl")]: {
      padding: isModernDesign ? 0 : `${theme.spacing(4)} ${gutter.xl}`,
    },
    [theme.breakpoints.up("xxl")]: {
      padding: isModernDesign ? 0 : `${theme.spacing(5)} ${gutter.xxl}`,
    },
    [theme.breakpoints.up("xxxl")]: {
      padding: isModernDesign ? 0 : `${theme.spacing(6)} ${gutter.xxxl}`,
    },
    "--swiper-navigation-size": "32px",
    "--swiper-theme-color": "#ffffff",
    "--swiper-navigation-sides-offset": "10px",
    "--swiper-navigation-hover-sides-offset": "3px",
  };
});

/** `nb` — the clipped surface with its bottom scrim. */
const Surface = styled("div", {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  transition: "box-shadow 0.3s ease-in-out",
  borderRadius: isModernDesign ? 0 : "20px",
  [theme.breakpoints.up("xxs")]: { borderRadius: isModernDesign ? 0 : "24px" },
  [theme.breakpoints.up("sm")]: { borderRadius: isModernDesign ? 0 : "28px" },
  [theme.breakpoints.up("xl")]: { borderRadius: isModernDesign ? 0 : "32px" },
  "&:hover": { boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 40%, transparent 70%)",
    pointerEvents: "none",
    zIndex: Z_INDEX.LOW,
  },
}));

/** `nw` — the Modern-Vibrant frame. Its own ratios, and no gutter ladder. */
const VibrantFrame = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  zIndex: Z_INDEX.NONE,
  aspectRatio: "16 / 9",
  padding: PAGE_PADDING,
  [theme.breakpoints.up("xxs")]: { padding: theme.spacing(2) },
  [theme.breakpoints.up("md")]: { aspectRatio: "22 / 9" },
  "--swiper-navigation-size": "32px",
  "--swiper-theme-color": "#ffffff",
  "--swiper-navigation-sides-offset": "10px",
  "--swiper-navigation-hover-sides-offset": "3px",
}));

/** `nZ` — note the radius DROPS again at `xl` (38 → 22). Verbatim. */
const VibrantSurface = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  transition: "box-shadow 0.3s ease-in-out",
  borderRadius: "20px",
  [theme.breakpoints.up("sm")]: { borderRadius: "38px" },
  [theme.breakpoints.up("xl")]: { borderRadius: "22px" },
  "&:hover": { boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)" },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.05) 40%, transparent 70%)",
    pointerEvents: "none",
    zIndex: Z_INDEX.LOW,
  },
}));

const AUTOPLAY = { delay: 5000, disableOnInteraction: false };

/** Slide art. `alt` is the literal "Slider" in the original. */
const slides = (images: string[]) =>
  images.map((image, index) => (
    <SwiperSlide key={index}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={image}
        alt="Slider"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </SwiperSlide>
  ));

export interface MainSliderProps {
  images?: string[] | null;
  /**
   * THE EMPTY STATE — see the note below. One real, already-configured image
   * to fall back on when the gallery is empty. Never a placeholder.
   */
  fallbackImage?: string | null;
  isModernDesign?: boolean;
  isModernVibrantDesign?: boolean;
  isLargerThanMobile?: boolean;
}

export default function MainSlider({
  images,
  fallbackImage,
  isModernDesign,
  isModernVibrantDesign,
  isLargerThanMobile,
}: MainSliderProps) {
  /**
   * DEFECT, fixed: the original's own test is `null != u && u.length > 0`, so
   * an array of EMPTY STRINGS passes it and Swiper is handed slides whose
   * `src` is "". That renders a broken-image glyph on a full-width hero. The
   * field is a free-form string array on the tenant record and the admin's
   * gallery uploader writes into it, so an empty entry is reachable. Filtered
   * to non-blank strings before the length test rather than after it.
   */
  const configured = (images ?? []).filter(
    (image): image is string => typeof image === "string" && image.trim() !== "",
  );

  /**
   * THE EMPTY STATE.
   *
   * `settings.web_slider_images` is `[]` on this tenant, which is a
   * CONFIGURATION gap and not a bug — nothing has been uploaded to the
   * gallery. The original's answer is a bare `Stack spacing={1.5}`: an empty
   * spacer with no children, i.e. a hero-shaped hole where a hero should be.
   *
   * `web_cover_image` IS set on this tenant (it is the landing page's hero),
   * and it is real, operator-supplied art of the right shape. Using it as the
   * single slide when the gallery is empty is the sensible state the brief
   * asks for: one full-width banner, exactly as the reference site does, from
   * real data — no placeholder, no invented asset.
   *
   * With NEITHER set there is genuinely nothing to show, and the original's
   * spacer is what is rendered. It is deliberately not a "no images yet" panel:
   * this is a customer-facing storefront, and a customer cannot fix it.
   */
  const list =
    configured.length > 0
      ? configured
      : fallbackImage && fallbackImage.trim() !== ""
        ? [fallbackImage]
        : [];

  if (list.length === 0) return <Stack spacing={1.5} />;

  /**
   * DEFECT, fixed: Swiper's `loop` needs more slides than it shows, and with
   * one slide it logs "Loop Warning: The number of slides is not enough for
   * loop mode" and disables itself — while `autoplay` and the navigation
   * arrows stay mounted, so the hero carries two dead arrows over a single
   * static image. A one-slide hero is now simply a one-slide hero.
   */
  const isCarousel = list.length > 1;

  if (isModernVibrantDesign) {
    return (
      <VibrantFrame>
        <VibrantSurface>
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            modules={[Navigation, Autoplay, EffectFade]}
            navigation={isCarousel}
            effect="fade"
            style={{ width: "100%", height: "100%" }}
            loop={isCarousel}
            autoplay={isCarousel ? AUTOPLAY : false}
          >
            {slides(list)}
          </Swiper>
        </VibrantSurface>
      </VibrantFrame>
    );
  }

  const aspectRatio = isModernDesign
    ? isLargerThanMobile
      ? "29 / 9"
      : "22 / 9"
    : "16 / 9";

  return (
    <Frame sx={{ aspectRatio }} isModernDesign={isModernDesign}>
      <Surface isModernDesign={isModernDesign}>
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          modules={[Navigation, Autoplay]}
          navigation={isCarousel}
          style={{ width: "100%", height: "100%" }}
          loop={isCarousel}
          autoplay={isCarousel ? AUTOPLAY : false}
        >
          {slides(list)}
        </Swiper>
      </Surface>
    </Frame>
  );
}

/** Kept in scope so the non-modern gutter ladder above is greppable. */
export { CONTENT_GUTTER };
